import React, { useEffect, useState, useRef, useCallback } from "react";
import { 
  Box, 
  Typography, 
  Avatar, 
  Divider, 
  IconButton, 
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { ChevronLeft, ChevronRight, DragIndicator, Reorder } from "@mui/icons-material";
import LinkedInIcon from '@mui/icons-material/LinkedIn';


const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "DONNA",
  lastName: "STROUPE",
  currentPosition: "Sales Representative",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "123 Anywhere St, Any City",
  profile: "I am a Sales Representative who initiates and manages customer relationships. I ensure smooth transitions from initial outreach to final purchase, either by them or someone in their household.",
  linkedIn: "",
};

// Separate section orders for left and right sides
const defaultLeftSectionOrder = [
  'contact',
   'websites', 
  'education',
  'skills',
  'languages',
  'interests',
  'additionalInfo'
];

const defaultRightSectionOrder = [
  'profile',
  'workExperience',
  'certifications',
  'accomplishments',
  'volunteering',
  'references'
];

const ResumeTemplate10 = ({ 
  color = "#2b2320",
  font = "Arial, sans-serif",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "24px",
  sectionSpacing = 30,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 30,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  theme,
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const data = { ...defaultData, ...formData };
  
  // Global font style for all text
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    // KEY FIX: word wrap for all text
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  };

  // Style object for consistent font family application
  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  // Resolve primaryColor from multiple possible shapes (theme may be string or object)
  const resolvePrimary = () => {
    if (!theme && color) return color;
    if (typeof theme === "string") return theme;
    if (theme && typeof theme === "object") {
      if (theme.primary) return theme.primary;
      if (theme.color) return theme.color;
      if (theme.hex) return theme.hex;
    }
    return color;
  };

  const primaryColor = resolvePrimary();
  const darkColor = (theme && (theme.dark || theme.secondary)) || getDarkColor(primaryColor);
  
  useEffect(() => {
    console.log("Resume10 - theme/color update:", { theme, color, primaryColor, darkColor });
  }, [theme, color, primaryColor, darkColor]);
  
  // State for all resume data
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const mainContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  // State for section ordering - separate for left and right
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [draggedSection, setDraggedSection] = useState(null);
  
  // State for arrange sections dialog
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Helper function to darken colors
  function getDarkColor(hex) {
    let c = hex.replace('#', '');
    if (c.length === 8) c = c.slice(0, 6);
    let r = Math.max(0, parseInt(c.substring(0,2),16) - 40);
    let g = Math.max(0, parseInt(c.substring(2,4),16) - 40);
    let b = Math.max(0, parseInt(c.substring(4,6),16) - 40);
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  }

  useEffect(() => {
    // Load data from localStorage
    const loadEducationEntries = () => {
      const storedEntries = localStorage.getItem("educationEntries");
      if (storedEntries) {
        try {
          const parsedEntries = JSON.parse(storedEntries);
          setEducationEntries(parsedEntries);
        } catch (error) {
          console.error("Error parsing education entries:", error);
          setEducationEntries([]);
        }
      } else {
        setEducationEntries([]);
      }
    };

    loadEducationEntries();

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      setSavedSkills(JSON.parse(storedSkills));
    }

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) {
      setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    }

    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);

    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);

    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);

    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);

    let storedSoftwareSkills = [];
    if (localStorage.getItem("softwareSkills")) {
      storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills"));
    }
    setSoftwareSkills(storedSoftwareSkills);

    const referenceKeys = ["references", "referencesList", "referenceList", "savedReferences"];
    let rawRefs = [];
    for (const k of referenceKeys) {
      const v = localStorage.getItem(k);
      if (v) {
        try { rawRefs = JSON.parse(v); break; } catch (e) { continue; }
      }
    }
    const normalizeReferences = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.map(r => {
        if (typeof r === "string") return { name: r };
        if (typeof r === "object" && r !== null) {
          return {
            name: r.name || r.referenceName || r.reference || "",
            position: r.position || r.jobTitle || r.currentPosition || "",
            company: r.company || r.companyName || "",
            phone: r.phone || r.contact || "",
            email: r.email || r.mail || ""
          };
        }
        return { name: String(r) };
      });
    };
    const normalizedRefs = normalizeReferences(rawRefs);
    setSavedReferences(normalizedRefs);
    
    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);
    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);
    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);

    const loadCustomSections = () => {
      const userId = localStorage.getItem("userId");
      let sectionsData = {};

      if (userId) {
        const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
        
        if (currentCustomSections.length > 0) {
          currentCustomSections.forEach(sectionName => {
            const userCustomKey = `custom_${userId}_${sectionName}`;
            try {
              const customSectionData = localStorage.getItem(userCustomKey);
              if (customSectionData) {
                const parsedData = JSON.parse(customSectionData);
                const items = parsedData.items || [];
                const validItems = items.filter(item => item && item.trim() !== '');
                if (validItems.length > 0) {
                  sectionsData[sectionName] = validItems;
                }
              }
            } catch (error) {
              console.error(`Error loading custom section ${sectionName}:`, error);
            }
          });
        }
      }

      return sectionsData;
    };

    const customSectionsData = loadCustomSections();
    setCustomSections(customSectionsData);

    const savedLeftSectionOrder = localStorage.getItem("resume10LeftSectionOrder");
    if (savedLeftSectionOrder) {
      const parsedOrder = JSON.parse(savedLeftSectionOrder);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      setLeftSectionOrder(filteredOrder);
    } else {
      setLeftSectionOrder(defaultLeftSectionOrder);
    }

    const savedRightSectionOrder = localStorage.getItem("resume10RightSectionOrder");
    if (savedRightSectionOrder) {
      const parsedOrder = JSON.parse(savedRightSectionOrder);
      const currentCustomSectionIds = Object.keys(customSectionsData).map(name => `custom_${name}`);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      const allRightSections = [...filteredOrder, ...currentCustomSectionIds];
      setRightSectionOrder(allRightSections);
    } else {
      const currentCustomSectionIds = Object.keys(customSectionsData).map(name => `custom_${name}`);
      const updatedRightSectionOrder = [...defaultRightSectionOrder, ...currentCustomSectionIds];
      setRightSectionOrder(updatedRightSectionOrder);
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredRightOrder = rightSectionOrder.filter(section => !section.startsWith('custom_'));
      const newRightOrder = [...filteredRightOrder, ...currentCustomSectionIds];
      if (JSON.stringify(newRightOrder) !== JSON.stringify(rightSectionOrder)) {
        setRightSectionOrder(newRightOrder);
        localStorage.setItem("resume10RightSectionOrder", JSON.stringify(newRightOrder));
      }
      const filteredLeftOrder = leftSectionOrder.filter(section => !section.startsWith('custom_'));
      if (JSON.stringify(filteredLeftOrder) !== JSON.stringify(leftSectionOrder)) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume10LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const filteredRightOrder = rightSectionOrder.filter(section => !section.startsWith('custom_'));
      if (filteredRightOrder.length !== rightSectionOrder.length) {
        setRightSectionOrder(filteredRightOrder);
        localStorage.setItem("resume10RightSectionOrder", JSON.stringify(filteredRightOrder));
      }
      const filteredLeftOrder = leftSectionOrder.filter(section => !section.startsWith('custom_'));
      if (filteredLeftOrder.length !== leftSectionOrder.length) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume10LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    }
  }, [customSections]);

  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume10LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume10RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const getEducationDisplayText = (entry) => {
    if (entry.educationLevel === "school") {
      return `${entry.standard} • ${entry.schoolName}`;
    } else {
      return `${entry.degreeName} • ${entry.collegeName}`;
    }
  };

  const getEducationDetails = (entry) => {
    if (entry.educationLevel === "school") {
      return `📍 ${entry.schoolLocation} • 📊 ${entry.percentage}% • 🏛️ ${entry.district}`;
    } else {
      return `📍 ${entry.collegeLocation} • 🎓 ${entry.specialization || entry.degreeType}`;
    }
  };

  // Drag and drop handlers
  const handleLeftDragStart = (e, index) => {
    const sectionData = { index, side: 'left', sectionId: leftSectionOrder[index] };
    e.dataTransfer.setData('text/plain', JSON.stringify(sectionData));
    setDraggedSection(sectionData);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    const sectionData = { index, side: 'right', sectionId: rightSectionOrder[index] };
    e.dataTransfer.setData('text/plain', JSON.stringify(sectionData));
    setDraggedSection(sectionData);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;

    if (sourceSide === 'left' && sourceIndex !== targetIndex) {
      const newSectionOrder = [...leftSectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setLeftSectionOrder(newSectionOrder);
    } else if (sourceSide === 'right') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      const removedSection = newRightSectionOrder.splice(sourceIndex, 1)[0];
      newLeftSectionOrder.splice(targetIndex, 0, removedSection);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    setDraggedSection(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;

    if (sourceSide === 'right' && sourceIndex !== targetIndex) {
      const newSectionOrder = [...rightSectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setRightSectionOrder(newSectionOrder);
    } else if (sourceSide === 'left') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      const removedSection = newLeftSectionOrder.splice(sourceIndex, 1)[0];
      newRightSectionOrder.splice(targetIndex, 0, removedSection);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    setDraggedSection(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    setDraggedSection(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => setRightSectionOrder(defaultRightSectionOrder);
  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  // ─── OVERFLOW FIX: common style injected into every section wrapper ───────────
  const sectionWrapStyle = {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    boxSizing: "border-box",
  };

  // Shared Typography sx for overflow-safe text
  const overflowTextSx = {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    maxWidth: "100%",
    overflow: "hidden",
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // Render sidebar sections based on current order
  const renderSidebarSections = () => {
    return leftSectionOrder.map(sectionId => {
      switch (sectionId) {
        case 'contact':
          return (data.phone || data.email || data.city || data.linkedIn || savedWebsites.length > 0) ? (
            <div key="contact" className="sidebar-section" style={sectionWrapStyle}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: "bold", 
                mt: sectionSpacing / 10,
                fontSize: `calc(${fontSize} * 1.1)`,
                fontFamily: font,
                fontStyle: fontStyle,
                ...overflowTextSx,
              }}>
                CONTACT
              </Typography>
              <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              <Box sx={{ fontSize: fontSize, mb: sectionSpacing / 10, ...sectionWrapStyle }}>
                {data.email && (
                  <Typography sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>
                    📧 {data.email}
                  </Typography>
                )}
                {data.phone && (
                  <Typography sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>
                    📞 {data.phone}
                  </Typography>
                )}
                {data.city && (
                  <Typography sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>
                    📍 {data.city}
                  </Typography>
                )}
                {data.linkedIn && (
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: paragraphSpacing / 10, ...getTextStyle(), ...overflowTextSx }}>
                    <LinkedInIcon sx={{ fontSize: 16, color: '#0077b5', flexShrink: 0 }} />
                    <a 
                      href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: 'inherit', 
                        textDecoration: 'none',
                        fontFamily: font,
                        fontSize: fontSize,
                        wordBreak: "break-all",
                        overflow: "hidden",
                        maxWidth: "100%",
                      }}
                    >
                      {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                    </a>
                  </Typography>
                )}
              
              </Box>
            </div>
          ) : null;

        case 'education':
          return educationEntries.length > 0 ? (
            <div key="education" className="sidebar-section" style={sectionWrapStyle}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: "bold", 
                mt: sectionSpacing / 10,
                fontSize: `calc(${fontSize} * 1.1)`,
                fontFamily: font,
                fontStyle: fontStyle,
                ...overflowTextSx,
              }}>
                EDUCATION
              </Typography>
              <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              {educationEntries.map((edu, index) => (
                <Box key={index} sx={{ mb: 2, ...sectionWrapStyle }}>
                  <Typography variant="body2" sx={{ 
                    fontFamily: font,
                    fontSize: `calc(${fontSize} * 1.05)`,
                    fontWeight: "bold",
                    fontStyle: fontStyle,
                    mb: paragraphSpacing / 20,
                    ...overflowTextSx,
                  }}>
                    {edu.educationLevel === "school" ? (
                      <>{edu.standard || "School"}{edu.stream && ` - ${edu.stream}`}</>
                    ) : (
                      <>{edu.degreeName || "Degree"}{edu.specialization && ` - ${edu.specialization}`}</>
                    )}
                  </Typography>
                  <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 20, ...overflowTextSx })}>
                    {edu.educationLevel === "school" ? edu.schoolName : edu.collegeName}
                  </Typography>
                  {edu.educationLevel === "college" && edu.programType && (
                    <Typography variant="body2" sx={getTextStyle({ fontStyle: 'italic', mb: paragraphSpacing / 20, ...overflowTextSx })}>
                      {edu.programType}
                    </Typography>
                  )}
                  {(edu.gradMonth && edu.gradYear) && (
                    <Typography variant="body2" sx={getTextStyle({ fontStyle: 'italic', mb: paragraphSpacing / 20, ...overflowTextSx })}>
                      {formatDate(edu.gradMonth, edu.gradYear)}
                    </Typography>
                  )}
                  {(edu.percentage || edu.cgpa || edu.grade) && (
                    <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 20, ...overflowTextSx })}>
                      {edu.percentage && `Score: ${edu.percentage}%`}
                      {edu.cgpa && `CGPA: ${edu.cgpa}`}
                      {edu.grade && `Grade: ${edu.grade}`}
                    </Typography>
                  )}
                </Box>
              ))}
            </div>
          ) : null;

        case 'skills':
          return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
            <div key="skills" className="sidebar-section" style={sectionWrapStyle}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: "bold", 
                mt: sectionSpacing / 10,
                fontSize: `calc(${fontSize} * 1.1)`,
                fontFamily: font,
                fontStyle: fontStyle,
                ...overflowTextSx,
              }}>
                SKILLS
              </Typography>
              <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              <ul style={{ 
                paddingLeft: "20px", 
                fontSize: fontSize,
                fontFamily: font,
                fontStyle: fontStyle,
                marginTop: paragraphSpacing / 10,
                marginBottom: paragraphSpacing / 10,
                maxWidth: "100%",
                boxSizing: "border-box",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}>
                {savedSkills.map((skill, index) => (
                  <li key={index} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{skill.name} {skill.rating ? `(${skill.rating}%)` : ""}</li>
                ))}
                {softwareSkills.map((skill, index) => (
                  <li key={`software-${index}`} style={{ ...globalFontStyle, wordBreak: "break-word" }}>
                    {skill.name} {skill.level ? `(${skill.level}%)` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null;

        case 'languages':
          return savedLanguages.length > 0 ? (
            <div key="languages" className="sidebar-section" style={sectionWrapStyle}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: "bold", 
                mt: sectionSpacing / 10,
                fontSize: `calc(${fontSize} * 1.1)`,
                fontFamily: font,
                fontStyle: fontStyle,
                ...overflowTextSx,
              }}>
                LANGUAGES
              </Typography>
              <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              {savedLanguages.map((lang, index) => (
                <Typography key={index} variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>
                  {lang.name} {lang.level ? `(${lang.level})` : ""}
                </Typography>
              ))}
            </div>
          ) : null;

        case 'interests':
          return savedInterests.length > 0 ? (
            <div key="interests" className="sidebar-section" style={sectionWrapStyle}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: "bold", 
                mt: sectionSpacing / 10,
                fontSize: `calc(${fontSize} * 1.1)`,
                fontFamily: font,
                fontStyle: fontStyle,
                ...overflowTextSx,
              }}>
                INTERESTS
              </Typography>
              <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              <ul style={{ 
                paddingLeft: "20px", 
                fontSize: fontSize,
                fontFamily: font,
                fontStyle: fontStyle,
                marginTop: paragraphSpacing / 10,
                marginBottom: paragraphSpacing / 10,
                maxWidth: "100%",
                boxSizing: "border-box",
                wordBreak: "break-word",
              }}>
                {savedInterests.map((interest, index) => (
                  <li key={index} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{interest}</li>
                ))}
              </ul>
            </div>
          ) : null;

        case 'additionalInfo':
          return savedAdditionalInfo.length > 0 ? (
            <div key="additionalInfo" className="sidebar-section" style={sectionWrapStyle}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: "bold", 
                mt: sectionSpacing / 10,
                fontSize: `calc(${fontSize} * 1.1)`,
                fontFamily: font,
                fontStyle: fontStyle,
                ...overflowTextSx,
              }}>
                ADDITIONAL INFO
              </Typography>
              <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              <ul style={{ 
                paddingLeft: "20px", 
                fontSize: fontSize,
                fontFamily: font,
                fontStyle: fontStyle,
                marginTop: paragraphSpacing / 10,
                marginBottom: paragraphSpacing / 10,
                maxWidth: "100%",
                boxSizing: "border-box",
                wordBreak: "break-word",
              }}>
                {savedAdditionalInfo.map((info, index) => (
                  <li key={index} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{info}</li>
                ))}
              </ul>
            </div>
          ) : null;

        default:
          return renderMainContentSectionsForSidebar(sectionId);
      }
    }).filter(Boolean);
  };

  // Render main content sections for sidebar (when moved from right to left)
  const renderMainContentSectionsForSidebar = (sectionId) => {
    switch (sectionId) {
      case 'profile':
        return (savedSummary || data.profile) ? (
          <div key="profile" className="sidebar-section" style={sectionWrapStyle}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              PROFILE
            </Typography>
            <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            <Typography variant="body2" sx={getTextStyle({ mb: sectionSpacing / 10, ...overflowTextSx })}>
              {savedSummary || data.profile}
            </Typography>
          </div>
        ) : null;
case 'websites':
  return savedWebsites.length > 0 ? (
    <div key="websites" className="sidebar-section" style={sectionWrapStyle}>
      <Typography variant="subtitle1" sx={{ 
        fontWeight: "bold", 
        mt: sectionSpacing / 10,
        fontSize: `calc(${fontSize} * 1.1)`,
        fontFamily: font,
        fontStyle: fontStyle,
        ...overflowTextSx,
      }}>
        WEBSITES
      </Typography>
      <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
      {savedWebsites.map((website, index) => (
        <Typography key={index} sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>
          🌐 {website.label ? `${website.label}: ` : ''}{website.url || website}
        </Typography>
      ))}
    </div>
  ) : null;
      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className="sidebar-section" style={sectionWrapStyle}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              WORK EXPERIENCE
            </Typography>
            <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            {workExperiences.map((work, index) => (
              <Box key={index} sx={{ mb: 2, ...sectionWrapStyle }}>
                <Typography variant="body2" sx={{ fontFamily: font, fontSize: fontSize, fontWeight: "bold", mb: paragraphSpacing / 20, fontStyle: fontStyle, ...overflowTextSx }}>
                  {work.jobTitle}
                </Typography>
                <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 20, ...overflowTextSx })}>
                  {work.employer}
                </Typography>
                <Typography variant="body2" sx={getTextStyle({ fontStyle: 'italic', mb: paragraphSpacing / 20, ...overflowTextSx })}>
                  {formatDate(work.startMonth, work.startYear)} -{" "}
                  {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className="sidebar-section" style={sectionWrapStyle}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              CERTIFICATIONS
            </Typography>
            <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ mb: 2, ...sectionWrapStyle }}>
                <Typography variant="body2" sx={{ fontFamily: font, fontSize: fontSize, fontWeight: "bold", fontStyle: fontStyle, ...overflowTextSx }}>
                  {cert.name}
                </Typography>
                <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
                  {cert.provider} {cert.year && `(${cert.year})`}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className="sidebar-section" style={sectionWrapStyle}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              ACCOMPLISHMENTS
            </Typography>
            <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            <ul style={{ paddingLeft: "20px", fontSize: fontSize, fontFamily: font, fontStyle: fontStyle, marginBottom: sectionSpacing / 10, maxWidth: "100%", boxSizing: "border-box", wordBreak: "break-word" }}>
              {savedAccomplishments.map((acc, index) => (
                <li key={index} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{acc}</li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className="sidebar-section" style={sectionWrapStyle}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              VOLUNTEERING
            </Typography>
            <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: 2, ...sectionWrapStyle }}>
                <Typography variant="body2" sx={{ fontFamily: font, fontSize: fontSize, fontWeight: "bold", fontStyle: fontStyle, ...overflowTextSx }}>
                  {vol.subtopic}
                </Typography>
                <Typography variant="body2" sx={getTextStyle({ fontStyle: 'italic', ...overflowTextSx })}>
                  {vol.fromDate} - {vol.toDate}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className="sidebar-section" style={sectionWrapStyle}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              REFERENCES
            </Typography>
            <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            {savedReferences.map((ref, index) => (
              <Box key={index} sx={{ mb: 2, ...sectionWrapStyle }}>
                <Typography variant="body2" sx={{ fontFamily: font, fontWeight: "bold", fontStyle: fontStyle, fontSize: fontSize, ...overflowTextSx }}>
                  {ref.name || ref.referenceName || `Reference ${index + 1}`}
                </Typography>
                <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
                  {ref.position || ref.jobTitle} {ref.company && `at ${ref.company}`}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          if (items && items.length > 0) {
            return (
              <div key={sectionId} className="sidebar-section" style={sectionWrapStyle}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                  {customSectionName.toUpperCase()}
                </Typography>
                <Divider sx={{ my: 1, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
                <ul style={{ paddingLeft: "20px", fontSize: fontSize, fontFamily: font, fontStyle: fontStyle, marginTop: paragraphSpacing / 10, marginBottom: paragraphSpacing / 10, maxWidth: "100%", boxSizing: "border-box", wordBreak: "break-word" }}>
                  {items.map((item, i) => (
                    <li key={i} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return null;
        }
        return null;
    }
  };

  // Render main content sections based on current order
  const renderMainContentSections = () => {
    return rightSectionOrder.map(sectionId => {
      switch (sectionId) {
        case 'profile':
          return (savedSummary || data.profile) ? (
            <div key="profile" className="main-section" style={sectionWrapStyle}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                About Me
              </Typography>
              <Typography variant="body2" sx={{ mb: sectionSpacing / 10, textIndent: `${paragraphIndent}px`, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                {savedSummary || data.profile}
              </Typography>
            </div>
          ) : null;

        case 'workExperience':
          return workExperiences && workExperiences.length > 0 ? (
            <div key="workExperience" className="main-section" style={sectionWrapStyle}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                Work Experience
              </Typography>
              <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              {workExperiences.map((work, index) => (
                <Box key={index} sx={{ ...sectionWrapStyle }}>
                  <Typography variant="subtitle2" sx={{ fontFamily: font, fontSize: `calc(${fontSize} * 1.05)`, mb: paragraphSpacing / 10, fontStyle: fontStyle, ...overflowTextSx }}>
                    {formatDate(work.startMonth, work.startYear)} -{" "}
                    {work.current ? "Present" : formatDate(work.endMonth, work.endYear)} | {work.employer} | {work.jobTitle}
                  </Typography>
                  {work.description && (
                    <Box
                      sx={{
                        "& strong": { fontWeight: "bold", fontFamily: font, fontSize: fontSize, fontStyle: fontStyle },
                        "& em": { fontStyle: "italic", fontFamily: font, fontSize: fontSize },
                        "& u": { textDecoration: "underline", fontFamily: font, fontSize: fontSize, fontStyle: fontStyle },
                        "& *": { fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: "break-word", overflowWrap: "break-word" },
                        ...globalFontStyle,
                        ...sectionWrapStyle,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: work.description
                          .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle};">$1</strong>`)
                          .replace(/\*(.*?)\*/g, `<em style="font-family: ${font}; font-size: ${fontSize};">$1</em>`)
                          .replace(/<u>(.*?)<\/u>/g, `<u style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle};">$1</u>`)
                          .replace(/\n/g, "<br/>"),
                      }}
                    />
                  )}
                </Box>
              ))}
            </div>
          ) : null;

        case 'certifications':
          return savedCertifications.length > 0 ? (
            <div key="certifications" className="main-section" style={sectionWrapStyle}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                Certifications
              </Typography>
              <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              {savedCertifications.map((cert, index) => (
                <Box key={index} sx={{ mb: 2, ...sectionWrapStyle }}>
                  <Typography variant="subtitle2" sx={{ fontFamily: font, fontSize: `calc(${fontSize} * 1.05)`, fontWeight: "bold", fontStyle: fontStyle, ...overflowTextSx }}>
                    {cert.name}
                  </Typography>
                  <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
                    {cert.provider} {cert.year && `(${cert.year})`}
                  </Typography>
                </Box>
              ))}
            </div>
          ) : null;

        case 'accomplishments':
          return savedAccomplishments.length > 0 ? (
            <div key="accomplishments" className="main-section" style={sectionWrapStyle}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                Accomplishments
              </Typography>
              <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              <ul style={{ paddingLeft: "20px", fontSize: fontSize, fontFamily: font, fontStyle: fontStyle, marginBottom: sectionSpacing / 10, maxWidth: "100%", boxSizing: "border-box", wordBreak: "break-word" }}>
                {savedAccomplishments.map((acc, index) => (
                  <li key={index} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{acc}</li>
                ))}
              </ul>
            </div>
          ) : null;

        case 'volunteering':
          return savedVolunteering.length > 0 ? (
            <div key="volunteering" className="main-section" style={sectionWrapStyle}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                Volunteering
              </Typography>
              <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              {savedVolunteering.map((vol, index) => (
                <Box key={index} sx={{ mb: 2, ...sectionWrapStyle }}>
                  <Typography variant="subtitle2" sx={{ fontFamily: font, fontSize: `calc(${fontSize} * 1.05)`, fontWeight: "bold", fontStyle: fontStyle, ...overflowTextSx }}>
                    {vol.subtopic}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: font, fontStyle: 'italic', ...overflowTextSx }}>
                    {vol.fromDate} - {vol.toDate}
                  </Typography>
                  <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
                    {vol.content}
                  </Typography>
                </Box>
              ))}
            </div>
          ) : null;

        case 'references':
          return savedReferences.length > 0 ? (
            <div key="references" className="main-section" style={sectionWrapStyle}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mt: sectionSpacing / 10, mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                References
              </Typography>
              <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", ...sectionWrapStyle }}>
                {savedReferences.map((ref, index) => (
                  <Box key={index} sx={{ mb: 2, minWidth: "45%", maxWidth: "48%", overflow: "hidden", wordBreak: "break-word" }}>
                    <Typography variant="subtitle2" sx={{ fontFamily: font, fontWeight: "bold", fontStyle: fontStyle, fontSize: `calc(${fontSize} * 1.05)`, ...overflowTextSx }}>
                      {ref.name || ref.referenceName || `Reference ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
                      {ref.position || ref.jobTitle} {ref.company && `at ${ref.company}`}
                    </Typography>
                    {ref.phone && (
                      <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
                        📞 {ref.phone}
                      </Typography>
                    )}
                    {ref.email && (
                      <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
                        📧 {ref.email}
                      </Typography>
                    )}
                    {ref.relationship && (
                      <Typography variant="body2" sx={getTextStyle({ fontStyle: 'italic', ...overflowTextSx })}>
                        Relationship: {ref.relationship}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </div>
          ) : null;

        default:
          return renderSidebarSectionsForMainContent(sectionId);
      }
    }).filter(Boolean);
  };

  // Render sidebar sections for main content (when moved from left to right)
  const renderSidebarSectionsForMainContent = (sectionId) => {
    switch (sectionId) {
      case 'contact':
        return (data.phone || data.email || data.city || data.linkedIn || savedWebsites.length > 0) ? (
          <div key="contact" className="main-section" style={sectionWrapStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              Contact
            </Typography>
            <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            <Box sx={{ fontSize: fontSize, mb: sectionSpacing / 10, ...sectionWrapStyle }}>
              {data.email && <Typography sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>📧 {data.email}</Typography>}
              {data.phone && <Typography sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>📞 {data.phone}</Typography>}
              {data.city && <Typography sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>📍 {data.city}</Typography>}
              {data.linkedIn && (
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: paragraphSpacing / 10, ...getTextStyle(), ...overflowTextSx }}>
                  <LinkedInIcon sx={{ fontSize: 16, color: '#0077b5', flexShrink: 0 }} />
                  <a href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`} target="_blank" rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: "break-all" }}>
                    {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                  </a>
                </Typography>
              )}
             
            </Box>
          </div>
        ) : null;
case 'websites':
  return savedWebsites.length > 0 ? (
    <div key="websites" className="main-section" style={sectionWrapStyle}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
        Websites
      </Typography>
      <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
      <Box sx={{ fontSize: fontSize, mb: sectionSpacing / 10, ...sectionWrapStyle }}>
        {savedWebsites.map((website, index) => (
          <Typography key={index} sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>
            🌐 {website.label ? `${website.label}: ` : ''}{website.url || website}
          </Typography>
        ))}
      </Box>
    </div>
  ) : null;
      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className="main-section" style={sectionWrapStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
              Education
            </Typography>
            <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            {educationEntries.map((edu, index) => (
              <Box key={index} sx={{ mb: 3, ...sectionWrapStyle }}>
                <Typography variant="body2" sx={{ display: 'inline-block', backgroundColor: edu.educationLevel === "school" ? "primary.light" : "secondary.light", color: "white", px: 1.5, py: 0.5, borderRadius: 1, fontSize: `calc(${fontSize} * 0.9)`, mb: 1, fontFamily: font, fontStyle: fontStyle, maxWidth: "100%" }}>
                  {edu.educationLevel === "school" ? "🏫 School" : "🎓 College"}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontFamily: font, fontSize: `calc(${fontSize} * 1.1)`, fontWeight: "bold", fontStyle: fontStyle, mb: paragraphSpacing / 10, ...overflowTextSx }}>
                  {getEducationDisplayText(edu)}
                </Typography>
                <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 10, ...overflowTextSx })}>
                  {getEducationDetails(edu)}
                </Typography>
                {edu.stream && <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 20, ...overflowTextSx })}><strong>Stream:</strong> {edu.stream}</Typography>}
                {edu.specialization && <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 20, ...overflowTextSx })}><strong>Specialization:</strong> {edu.specialization}</Typography>}
                {edu.educationLevel === "college" && edu.programType && <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 20, fontStyle: 'italic', ...overflowTextSx })}>{edu.programType}</Typography>}
                {(edu.percentage || edu.cgpa || edu.grade) && (
                  <Typography variant="body2" sx={getTextStyle({ mb: paragraphSpacing / 20, ...overflowTextSx })}>
                    <strong>Performance:</strong>{edu.percentage && ` ${edu.percentage}%`}{edu.cgpa && ` CGPA: ${edu.cgpa}`}{edu.grade && ` Grade: ${edu.grade}`}
                  </Typography>
                )}
                {(edu.gradMonth && edu.gradYear) && <Typography variant="body2" sx={getTextStyle({ fontStyle: 'italic', mb: paragraphSpacing / 20, ...overflowTextSx })}>Graduated: {formatDate(edu.gradMonth, edu.gradYear)}</Typography>}
                {edu.additionalCoursework && (
                  <Typography variant="body2" sx={getTextStyle({ mt: 1, p: 1, backgroundColor: "grey.50", borderRadius: 1, fontSize: `calc(${fontSize} * 0.95)`, ...overflowTextSx })}>
                    <strong>Additional Details:</strong> {edu.additionalCoursework}
                  </Typography>
                )}
              </Box>
            ))}
          </div>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <div key="skills" className="main-section" style={sectionWrapStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>Skills</Typography>
            <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ...sectionWrapStyle }}>
              {savedSkills.map((skill, index) => (
                <Paper key={index} elevation={1} sx={{ p: 1, bgcolor: '#f5f5f5', maxWidth: "100%" }}>
                  <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>{skill.name} {skill.rating ? `(${skill.rating}%)` : ""}</Typography>
                </Paper>
              ))}
              {softwareSkills.map((skill, index) => (
                <Paper key={`software-${index}`} elevation={1} sx={{ p: 1, bgcolor: '#f5f5f5', maxWidth: "100%" }}>
                  <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>{skill.name} {skill.level ? `(${skill.level}%)` : ""}</Typography>
                </Paper>
              ))}
            </Box>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className="main-section" style={sectionWrapStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>Languages</Typography>
            <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ...sectionWrapStyle }}>
              {savedLanguages.map((lang, index) => (
                <Paper key={index} elevation={1} sx={{ p: 1, bgcolor: '#f5f5f5', maxWidth: "100%" }}>
                  <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>{lang.name} {lang.level ? `(${lang.level})` : ""}</Typography>
                </Paper>
              ))}
            </Box>
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className="main-section" style={sectionWrapStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>Interests</Typography>
            <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ...sectionWrapStyle }}>
              {savedInterests.map((interest, index) => (
                <Paper key={index} elevation={1} sx={{ p: 1, bgcolor: '#f5f5f5', maxWidth: "100%" }}>
                  <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>{interest}</Typography>
                </Paper>
              ))}
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className="main-section" style={sectionWrapStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>Additional Information</Typography>
            <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
            <ul style={{ paddingLeft: "20px", fontSize: fontSize, fontFamily: font, fontStyle: fontStyle, marginBottom: sectionSpacing / 10, maxWidth: "100%", boxSizing: "border-box", wordBreak: "break-word" }}>
              {savedAdditionalInfo.map((info, index) => (
                <li key={index} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{info}</li>
              ))}
            </ul>
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          if (items && items.length > 0) {
            return (
              <div key={sectionId} className="main-section" style={sectionWrapStyle}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
                  {customSectionName.toUpperCase()}
                </Typography>
                <Divider sx={{ mb: 2, borderWidth: `${lineWeight}px`, backgroundColor: primaryColor }} />
                <ul style={{ paddingLeft: "20px", fontSize: fontSize, fontFamily: font, fontStyle: fontStyle, marginBottom: sectionSpacing / 10, maxWidth: "100%", boxSizing: "border-box", wordBreak: "break-word" }}>
                  {items.map((item, i) => (
                    <li key={i} style={{ ...globalFontStyle, wordBreak: "break-word" }}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return null;
        }
        return null;
    }
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'contact': 'Contact', 'websites': 'Websites','education': 'Education', 'skills': 'Skills',
      'languages': 'Languages', 'interests': 'Interests', 'additionalInfo': 'Additional Information',
      'profile': 'Profile', 'workExperience': 'Work Experience', 'certifications': 'Certifications',
      'accomplishments': 'Accomplishments', 'volunteering': 'Volunteering', 'references': 'References'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIndicator sx={{ mr: 1 }} />
          {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>({sections.length} sections)</Typography>
        </Typography>
        <Box
          sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1, minHeight: 200, backgroundColor: '#fafafa' }}
          onDragOver={(e) => { e.preventDefault(); if (draggedSection && draggedSection.side !== side) setDragOverSide(side); }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverSide(null); }}
          onDrop={(e) => {
            e.preventDefault();
            if (draggedSection && draggedSection.side !== side) {
              if (side === 'left') {
                const newLeft = [...leftSectionOrder];
                const newRight = [...rightSectionOrder];
                const removed = newRight.splice(draggedSection.index, 1)[0];
                newLeft.push(removed);
                setLeftSectionOrder(newLeft);
                setRightSectionOrder(newRight);
              } else {
                const newLeft = [...leftSectionOrder];
                const newRight = [...rightSectionOrder];
                const removed = newLeft.splice(draggedSection.index, 1)[0];
                newRight.push(removed);
                setLeftSectionOrder(newLeft);
                setRightSectionOrder(newRight);
              }
              setDraggedSection(null);
              setDragOverSide(null);
            }
          }}
        >
          {sections.length > 0 ? (
            sections.map((sectionId, index) => (
              <Paper
                key={sectionId}
                draggable
                onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index, side)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 2, mb: 1, display: 'flex', alignItems: 'center',
                  backgroundColor: dragOverIndex === index && dragOverSide === side ? '#e3f2fd' : draggedSection?.sectionId === sectionId ? '#fff3e0' : 'white',
                  border: dragOverIndex === index && dragOverSide === side ? '2px dashed #2196f3' : '1px solid #e0e0e0',
                  boxShadow: (dragOverIndex === index && dragOverSide === side) || draggedSection?.sectionId === sectionId ? 2 : 1,
                  cursor: 'grab', '&:active': { cursor: 'grabbing' },
                  transition: 'all 0.2s ease',
                  transform: draggedSection?.sectionId === sectionId ? 'scale(0.95)' : 'scale(1)',
                }}
              >
                <DragIndicator color="action" />
                <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
                <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
              </Paper>
            ))
          ) : (
            <Paper
              sx={{ p: 4, textAlign: 'center', backgroundColor: dragOverSide === side ? '#e3f2fd' : 'white', border: dragOverSide === side ? '2px dashed #2196f3' : '1px dashed #e0e0e0' }}
              onDragOver={(e) => handleDragOver(e, 0, side)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => side === 'left' ? handleLeftDrop(e, 0) : handleRightDrop(e, 0)}
            >
              <Typography variant="body2" color="textSecondary">
                {dragOverSide === side ? 'Drop section here' : 'No sections. Drag sections from the other side.'}
              </Typography>
            </Paper>
          )}
        </Box>
        <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
          Reset {side === 'left' ? 'Left' : 'Right'} Order
        </Button>
      </Box>
    );
  };

  // Add global font style to HTML content - injects a scoped <style> that resets
  // EVERY element inside the rendered block to use the correct font/size/spacing.
  // This is the only reliable way to override MUI's own inline styles that survive
  // after outerHTML is captured and re-injected via dangerouslySetInnerHTML.
  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';

    // Unique scope class so the rules don't bleed into the rest of the page
    const scope = 'r10-content';

    const css = `
      <style>
        .${scope} * {
          font-family: ${font} !important;
          font-style: ${fontStyle} !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          word-break: break-word !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        /* Body / paragraph text */
        .${scope} p,
        .${scope} span,
        .${scope} div,
        .${scope} li,
        .${scope} a,
        .${scope} td,
        .${scope} th {
          font-size: ${fontSize} !important;
          line-height: ${lineSpacing}px !important;
        }
        /* Section headings (h6 is what MUI variant="h6" renders) */
        .${scope} h1,
        .${scope} h2,
        .${scope} h3,
        .${scope} h4,
        .${scope} h5,
        .${scope} h6 {
          font-size: ${headingSize} !important;
          line-height: calc(${headingSize} * 1.3) !important;
          margin-top: 0 !important;
          margin-bottom: ${paragraphSpacing / 10}px !important;
        }
        /* Section-level spacing: every direct .main-section / .sidebar-section child */
        .${scope} .main-section,
        .${scope} .sidebar-section {
          margin-bottom: ${sectionSpacing / 10}px !important;
        }
        /* Paragraph / item spacing */
        .${scope} p {
          margin-top: 0 !important;
          margin-bottom: ${paragraphSpacing / 10}px !important;
        }
        .${scope} li {
          margin-bottom: ${paragraphSpacing / 20}px !important;
        }
        /* Subtitle / body2 sized text (MUI renders these as <p>) */
        .${scope} .MuiTypography-subtitle1,
        .${scope} .MuiTypography-subtitle2 {
          font-size: calc(${fontSize} * 1.05) !important;
        }
        .${scope} .MuiTypography-body2 {
          font-size: ${fontSize} !important;
        }
        /* Dividers stay thin */
        .${scope} hr,
        .${scope} .MuiDivider-root {
          margin: 4px 0 !important;
        }
        /* Lists */
        .${scope} ul,
        .${scope} ol {
          font-size: ${fontSize} !important;
          line-height: ${lineSpacing}px !important;
          padding-left: 20px !important;
        }
      </style>
    `;

    // Wrap all content in the scoped div so the CSS rules apply
    return `${css}<div class="${scope}" style="width:100%;max-width:100%;overflow:hidden;word-break:break-word;box-sizing:border-box;">${htmlContent}</div>`;
  };

  // Pagination for sidebar content
  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sidebarContent = sidebarContentRef.current;
    const sections = Array.from(sidebarContent.querySelectorAll('.sidebar-section'));
    let newSidebarPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) newSidebarPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newSidebarPages.push([...currentPageSections]);
    });
    if (newSidebarPages.length === 0) newSidebarPages.push([]);
    setSidebarPages(newSidebarPages);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font]);

  // Pagination for main content (right side)
  const paginateMainContent = useCallback(() => {
    if (!mainContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const mainContent = mainContentRef.current;
    const sections = Array.from(mainContent.querySelectorAll('.main-section'));
    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) newPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newPages.push([...currentPageSections]);
    });
    if (newPages.length === 0) newPages.push([]);
    setPages(newPages);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [
    paginateSidebarContent, paginateMainContent,
    educationEntries, savedSkills, savedSummary, workExperiences,
    savedAdditionalInfo, savedLanguages, savedAccomplishments,
    savedCertifications, savedReferences, softwareSkills,
    savedVolunteering, savedInterests, savedWebsites,
    customSections, leftSectionOrder, rightSectionOrder, color,
    sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font,
  ]);

  // Render sidebar content for measurement (hidden)
  const renderSidebarForMeasurement = () => (
    <Box ref={sidebarContentRef} sx={{
      visibility: 'hidden', position: 'absolute', top: -9999, left: -9999,
      width: '30%', ...globalFontStyle,
      overflow: 'hidden', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word',
    }}>
      {renderSidebarSections()}
    </Box>
  );

  // Render main content for measurement (hidden)
  const renderMainContentForMeasurement = () => (
    <Box ref={mainContentRef} sx={{
      visibility: 'hidden', position: 'absolute', top: -9999, left: -9999,
      width: '70%', ...globalFontStyle,
      overflow: 'hidden', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word',
    }}>
      {renderMainContentSections()}
    </Box>
  );

  // Render actual sidebar content based on current page
  const renderSidebar = (pageIndex) => (
    <Box
      sx={{
        width: "30%",
        minWidth: "30%",
        maxWidth: "30%",
        flexShrink: 0,
        bgcolor: "#f0eae5",
        p: sideMargins / 5,
        fontFamily: font,
        fontSize: fontSize,
        fontStyle: fontStyle,
        // KEY FIX: contain everything inside sidebar
        overflow: "hidden",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        boxSizing: "border-box",
        "@media print": {
          width: "30%",
          display: "inline-block",
          verticalAlign: "top",
          fontFamily: font,
          fontSize: fontSize,
          fontStyle: fontStyle,
          overflow: "hidden",
          wordBreak: "break-word",
        },
      }}
    >
      {sidebarPages[pageIndex] ? (
        <div
          style={{
            ...globalFontStyle,
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            wordBreak: "break-word",
            boxSizing: "border-box",
          }}
          dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(sidebarPages[pageIndex].join('')) }}
        />
      ) : (
        <Typography sx={getTextStyle()}>Loading sidebar content...</Typography>
      )}
    </Box>
  );

  // Render header section
  const renderHeader = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: primaryColor,
        color: "white",
        p: sideMargins / 5,
        fontFamily: font,
        overflow: "hidden",
        "@media print": {
          bgcolor: primaryColor,
          WebkitPrintColorAdjust: "exact",
          printColorAdjust: "exact",
          fontFamily: font,
        }
      }}
    >
      <Avatar
        src={photo || "https://via.placeholder.com/150"}
        alt="Profile"
        sx={{ width: 90, height: 90, border: `${lineWeight * 3}px solid white`, mr: 2, flexShrink: 0 }}
      />
      <Box sx={{ overflow: "hidden", minWidth: 0, flex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: headingSize, fontFamily: font, fontStyle: fontStyle, ...overflowTextSx }}>
          {data.firstName} {data.lastName}
        </Typography>
        <Typography variant="body2" sx={getTextStyle({ ...overflowTextSx })}>
          {data.currentPosition}
        </Typography>
      </Box>
    </Box>
  );

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  return (
    <Box sx={{ width: "100%", position: "relative", m: 5 }}>
      {/* Page Navigation Controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {/* Hidden content for measurement */}
      {renderSidebarForMeasurement()}
      {renderMainContentForMeasurement()}
      
      {/* Visible resume content */}
      <Box id="resume10-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              boxShadow: pageIndex === currentPage ? 3 : 0,
              borderRadius: 2,
              // KEY FIX: prevent the page box itself from expanding
              overflow: "hidden",
              fontFamily: font,
              fontSize: fontSize,
              fontStyle: fontStyle,
              lineHeight: `${lineSpacing}px`,
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              flexDirection: "column",
              pageBreakAfter: "always",
              boxSizing: "border-box",
              "@media print": {
                width: page.width,
                height: "auto",
                minHeight: page.height,
                boxShadow: "none",
                m: 0,
                borderRadius: 0,
                display: 'flex',
                overflow: "hidden",
              },
            }}
          >
            {/* Header Section - Show ONLY on first page */}
            {pageIndex === 0 && renderHeader()}

            {/* KEY FIX: two-column layout with strict width control */}
            <Box sx={{
              display: "flex",
              flex: 1,
              width: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            }}>
              {/* Left Sidebar - strictly 30% */}
              {sidebarPages[pageIndex]
                ? renderSidebar(pageIndex)
                : <Box sx={{ width: "30%", minWidth: "30%", maxWidth: "30%", flexShrink: 0, bgcolor: "#f0eae5" }} />
              }
              
              {/* Right Content Section - takes remaining space, never overflows */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,           // KEY: allows flex child to shrink below content size
                  maxWidth: "70%",
                  p: sideMargins / 5,
                  marginTop: pageIndex > 0 ? `${topBottomMargin / 5}px` : `${topBottomMargin / 10}px`,
                  marginBottom: `${topBottomMargin / 10}px`,
                  fontFamily: font,
                  fontSize: fontSize,
                  fontStyle: fontStyle,
                  // KEY FIX: contain right-side content
                  overflow: "hidden",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  boxSizing: "border-box",
                  "@media print": {
                    display: "inline-block",
                    verticalAlign: "top",
                    fontFamily: font,
                    fontSize: fontSize,
                    fontStyle: fontStyle,
                    overflow: "hidden",
                    wordBreak: "break-word",
                  },
                }}
              >
                {pages[pageIndex] ? (
                  <div
                    style={{
                      ...globalFontStyle,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      boxSizing: "border-box",
                    }}
                    dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pages[pageIndex].join('')) }}
                  />
                ) : (
                  <Typography sx={getTextStyle()}>No content for this page</Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder them within each side or move sections between left sidebar and right content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

export default ResumeTemplate10;